<template>
	<div class="width-full relative input-wrapper">
		<span v-if="label" class="text-body-m text-neutral-900">
			{{ label }}
		</span>
		<div
			@click="openDatePicker"
			class="width-full d-flex align-center border-xs rounded-2 px-4 py-3 cursor-pointer bg-white"
			:class="[borderClasses, label ? 'mt-1' : '']"
			:style="{ height }"
		>
			<input
				ref="input"
				:value="modelValue"
				:name="name"
				:id="name"
				:type="inputType"
				:min="minDate"
				:max="maxDate"
				class="width-full text-neutral-900 d-flex flex-grow-1 width-12"
				style="font-weight: inherit; font-size: inherit; font-family: inherit"
				@focusout="focusOut"
				@input="sendDate"
			/>
			<PButton v-if="showIcon" type="text" color="neutral" :size="buttonSize">
				<Icon icon="calendar" />
			</PButton>
		</div>
		<div class="error-box" v-if="error">
			<p class="text-body-s text-error-500">
				{{ error }}
			</p>
		</div>
	</div>
</template>

<script setup lang="ts">
	import { ref, computed, nextTick } from 'vue'
	import { ButtonSize } from '~/common/passporter-styles/designSystem/button'

	interface Props {
		modelValue?: string
		name?: string
		placeholder?: string
		error?: string
		size?: 's' | 'm' | 'l'
		minDate?: string
		maxDate?: string
		showIcon?: boolean
		type?: 'date' | 'datetime'
		label?: string
	}

	const props = withDefaults(defineProps<Props>(), {
		name: '',
		placeholder: '',
		error: '',
		type: 'date',
		size: 'm',
		minDate: '',
		maxDate: '',
		label: '',
		showIcon: true,
		modelValue: undefined,
	})
	const emit = defineEmits(['update:modelValue', 'click', 'focus', 'focusout'])

	const input = ref<HTMLInputElement>()
	const isFocused = ref<boolean>(false)

	const sendDate = (event: any) => {
		const stringDate = (event.target! as HTMLInputElement).value
		emit('update:modelValue', stringDate)
	}

	const openDatePicker = () => {
		if (input.value && typeof input.value.showPicker === 'function') {
			input.value.showPicker()
		} else {
			input.value?.focus()
		}
		isFocused.value = true
		emit('click')
		emit('focus')
	}

	const focusOut = () => {
		isFocused.value = false
		emit('focusout')
	}

	const borderClasses = computed(() => {
		const errorBorder = ['border-error-500', 'input-text-error']
		const focusedBorder = ['border-neutral-700']
		const regularBorder = ['border-neutral-200']

		if (props.error) return errorBorder
		if (isFocused.value) return focusedBorder
		return regularBorder
	})

	const height = computed(() => {
		const heights = {
			s: '44px',
			m: '64px',
			l: '130px',
		}
		return heights[props.size]
	})

	const buttonSize = computed((): ButtonSize => {
		const sizes: Record<string, ButtonSize> = {
			s: 'sm',
			m: 'md',
			l: 'lg',
		}
		return sizes[props.size] || 'md'
	})

	const inputType = computed((): string => {
		if (props.type == 'datetime') return 'datetime-local'
		return 'date'
	})

	const focusInput = () => {
		input.value?.focus()
		isFocused.value = true
		emit('click')
		emit('focus')
		nextTick(() => openDatePicker())
	}
	defineExpose({ focusInput })
</script>

<style scoped lang="scss">
	.input-wrapper {
		min-width: 180px;
	}

	input {
		border: none;
	}

	input[type='datetime-local']::-webkit-calendar-picker-indicator,
	input[type='date']::-webkit-calendar-picker-indicator {
		display: none;
		-webkit-appearance: none;
	}

	input:focus {
		outline: none;
	}

	.input-text:hover {
		&:not(.input-text-error) {
			border-color: var(--color-neutral-500) !important;
		}
	}

	.error-box {
		height: 20px;
	}
</style>
